import { format } from 'date-fns';
import { TeamData, Employee, BudgetData, TeamPerformanceData } from '../contexts/AppContext.tsx';
import { TeamPerformanceService } from './teamPerformanceService.ts';
import { calculateAllTeamsTargetSales, saveTeamTargetSalesToMonthlyData } from './teamTargetSalesService.ts';
import { ActualSalesService } from './actualSalesService.ts';

import { logger } from '../utils/logger.ts';

export interface TeamPerformanceRefreshResult {
  performanceData: TeamPerformanceData[];
  totalActualSales: number;
  totalWorkingDays: number;  // 🆕 전체 근무일 (합집합)
  lastCalculatedAt: string;
  dataSource: 'cached' | 'calculated' | 'none';
  success: boolean;
  error?: string;
  // 🛡️ 오귀속 의심: 진료의사 매핑 부재 상태에서 계산된 결과 — 저장·웹훅 전송 금지, 표시만 허용
  suspectMisattribution?: boolean;
}

export interface TeamPerformanceRefreshOptions {
  forceRefresh?: boolean;
  saveData?: boolean;
  showLogs?: boolean;
}

/**
 * 팀 성과 데이터 새로고침을 위한 재사용 가능한 서비스
 * 다양한 페이지에서 팀 성과 데이터를 안전하게 새로고침할 수 있습니다.
 * 
 * 🔒 안전성 특징:
 * - null/undefined 객체 접근으로 인한 "Cannot convert undefined or null to object" 에러 방지
 * - 중복 실행 방지
 * - 포괄적인 에러 처리
 * - 안전한 데이터 변환
 */
export class TeamPerformanceRefreshService {
  private static isRefreshing = false;
  private static refreshPromise: Promise<TeamPerformanceRefreshResult> | null = null;

  /**
   * 팀 성과 데이터를 새로고침합니다
   * @param currentDate 현재 날짜
   * @param teams 팀 데이터
   * @param employees 직원 데이터
   * @param budgetData 예산 데이터
   * @param setBudgetData 예산 데이터 설정 함수
   * @param options 새로고침 옵션
   * @returns 새로고침 결과
   */
  static async refreshTeamPerformanceData(
    currentDate: Date,
    teams: TeamData[],
    employees: Employee[],
    budgetData: BudgetData,
    setBudgetData: (data: BudgetData | ((prev: BudgetData) => BudgetData)) => void,
    options: TeamPerformanceRefreshOptions = {}
  ): Promise<TeamPerformanceRefreshResult> {
    const {
      forceRefresh = false,
      saveData = true,
      showLogs = true
    } = options;

    // 중복 실행 방지
    if (this.isRefreshing && this.refreshPromise) {
      if (showLogs) {
        logger.debug('[TeamPerformanceRefreshService] 이미 새로고침이 진행 중입니다. 기존 Promise를 반환합니다.');
      }
      return this.refreshPromise;
    }

    // 필수 데이터 유효성 검사
    const validationResult = this.validateRequiredData(currentDate, teams, employees, budgetData);
    if (!validationResult.isValid) {
      return {
        performanceData: [],
        totalActualSales: 0,
        totalWorkingDays: 0,
        lastCalculatedAt: '',
        dataSource: 'none',
        success: false,
        error: validationResult.error
      };
    }

    this.isRefreshing = true;

    try {
      this.refreshPromise = this.performRefresh(
        currentDate,
        teams,
        employees,
        budgetData,
        setBudgetData,
        { forceRefresh, saveData, showLogs }
      );

      const result = await this.refreshPromise;
      return result;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  /**
   * 실제 새로고침 로직 수행
   */
  private static async performRefresh(
    currentDate: Date,
    teams: TeamData[],
    employees: Employee[],
    budgetData: BudgetData,
    setBudgetData: (data: BudgetData | ((prev: BudgetData) => BudgetData)) => void,
    options: TeamPerformanceRefreshOptions
  ): Promise<TeamPerformanceRefreshResult> {
    const { forceRefresh, saveData, showLogs } = options;
    const yearMonth = format(currentDate, 'yyyy-MM');

    try {
      if (showLogs) {
        logger.debug(`[TeamPerformanceRefreshService] ${yearMonth} 새로고침 시작 (강제: ${forceRefresh})`);
      }

      /**
       * 캐시 vs Firebase 우선순위 로직
       * - Firebase에 새 데이터가 있으면 → 계산 실행
       * - Firebase에 새 데이터가 없으면 → 캐시 사용
       * - Firebase 오류 시 → 캐시 폴백
       */
      if (!forceRefresh) {
        try {
          const hasNewerFirebaseData = await this.checkFirebaseForNewerData(yearMonth, budgetData, showLogs);

          if (!hasNewerFirebaseData) {
            // Firebase에 새 데이터 없음 → 캐시 사용
            const cachedResult = this.loadCachedData(yearMonth, budgetData, showLogs);
            if (cachedResult) {
              return cachedResult;
            }
          }
          // Firebase에 새 데이터 있음 → 아래 계산 로직 실행
        } catch (error) {
          logger.warn('[TeamPerformanceRefreshService] Firebase 확인 실패, 캐시 시도:', error);
          // Firebase 실패 시 캐시 폴백
          const cachedResult = this.loadCachedData(yearMonth, budgetData, showLogs);
          if (cachedResult) {
            return cachedResult;
          }
        }
      }

      // 🔥 Step 1: 팀별 목표매출 재계산 (급여 관리 로직 사용)
      if (showLogs) {
        logger.debug(`[TeamPerformanceRefreshService] ${yearMonth} 팀별 목표매출 재계산 시작`);
      }

      // ✅ 월별 직원 데이터 우선 사용 (급여 관리와 동일)
      const monthlyEmployees = budgetData.monthlyData?.[yearMonth]?.employees;
      const targetEmployees = (monthlyEmployees && monthlyEmployees.length > 0)
        ? monthlyEmployees
        : employees;

      if (showLogs) {
        logger.debug(`[TeamPerformanceRefreshService] 사용 직원 데이터:`, {
          소스: monthlyEmployees ? '월별 저장 데이터' : '전역 데이터',
          직원수: targetEmployees.length
        });
      }

      try {
        // 목표매출 계산 (지난달 미달성 조정 포함, 월별 직원 데이터 사용)
        const targetSalesResult = await calculateAllTeamsTargetSales(
          yearMonth,
          targetEmployees,  // ← 변경: employees → targetEmployees
          budgetData,
          {
            includePreviousMonthAdjustment: true,
            verbose: showLogs
          }
        );

        if (showLogs) {
          logger.debug(`[TeamPerformanceRefreshService] ${yearMonth} 목표매출 계산 완료:`, {
            팀수: Object.keys(targetSalesResult.teamTargetSales).length,
            총목표매출: targetSalesResult.totalTargetSales.toLocaleString() + '원'
          });
        }

        // 목표매출 데이터를 monthlyData에 저장
        if (saveData) {
          await saveTeamTargetSalesToMonthlyData(
            yearMonth,
            targetSalesResult
          );

          // FIX-C: 중간 setBudgetData 제거 — Step 2 saveTeamPerformanceData에서 통합 호출
          // (로컬 budgetData 변수는 아래에서 업데이트됨)
          if (showLogs) {
            logger.debug(`[TeamPerformanceRefreshService] ${yearMonth} 목표매출 Firebase 저장 완료 (React 상태는 Step 2에서 통합 업데이트)`);
          }
        }

        // budgetData를 업데이트한 최신 상태로 변경
        budgetData = {
          ...budgetData,
          monthlyData: {
            ...budgetData.monthlyData,
            [yearMonth]: {
              ...budgetData.monthlyData?.[yearMonth],
              teamTargetSales: targetSalesResult.teamTargetSales,
              teamPureTargetSales: targetSalesResult.teamPureTargetSales,
              teamSalaryTotals: targetSalesResult.teamSalaryTotals
            }
          }
        };

      } catch (targetSalesError) {
        logger.error(`[TeamPerformanceRefreshService] ${yearMonth} 목표매출 계산/저장 실패:`, targetSalesError);
        // 목표매출 계산 실패해도 팀 성과 계산은 계속 진행
      }

      // 🔥 Step 2: 팀 성과 데이터 계산 (업데이트된 budgetData 및 동일한 직원 데이터 사용)
      if (showLogs) {
        logger.debug(`[TeamPerformanceRefreshService] ${yearMonth} 팀 성과 데이터 계산 시작`);
      }

      const calculatedData = await TeamPerformanceService.calculateTeamPerformance(
        yearMonth,
        teams,
        targetEmployees,  // ← 변경: employees → targetEmployees (일관성 보장)
        budgetData,
        { forceRefresh }
      );

      // 안전한 데이터 변환
      const performanceArray = this.safeObjectToArray(calculatedData.teamPerformanceData);
      const totalActualSales = this.safeNumber(calculatedData.totalActualSales);
      const totalWorkingDays = this.safeNumber(calculatedData.totalWorkingDays);  // 🆕 전체 근무일
      const lastCalculatedAt = new Date().toISOString();

      // 🔧 actualSales 캐시 병합 (파이프라인 소유 필드): Firebase 캐시는 calculateTeamPerformance
      // 내부의 saveActualSalesData가 이미 저장했다(오귀속 의심 시 스킵). 여기서는
      // (1) 이 함수 안의 로컬 budgetData 스냅샷을 최신으로 유지하고,
      // (2) 아래 저장 게이트(saveData && !suspectMisattribution)에서 React 상태에도 함수형
      //     패치로 반영한다 — saveTeamPerformanceData의 함수형 패치는 자기 소유 필드
      //     (teamPerformanceData·totalActualSales·calculatedAt)만 갱신해 actualSales를
      //     건드리지 않으므로, 상태 반영이 없으면 다음 refresh가 캐시 미스로 재계산을 반복한다.
      let refreshedActualSales: Record<string, any> | null = null;
      if (calculatedData.actualSalesResult) {
        // saveActualSalesData가 Firebase에 저장하는 캐시와 동일한 형태로 구성
        // (빈 결과는 validatedEmpty 마커 유지 — 빈캐시 무효화→재계산 루프 방지)
        const isEmptyResult =
          Object.keys(calculatedData.actualSalesResult.teamActualSales || {}).length === 0;
        refreshedActualSales = {
          teamActualSales: calculatedData.actualSalesResult.teamActualSales,
          totalActualSales: calculatedData.actualSalesResult.totalActualSales,
          assistDetails: calculatedData.actualSalesResult.assistDetails,
          calculatedAt: calculatedData.actualSalesResult.calculatedAt,
          dataSource: calculatedData.actualSalesResult.dataSource,
          isValidated: calculatedData.actualSalesResult.isValidated,
          ...(isEmptyResult ? { validatedEmpty: true } : {})
        };

        const currentMonthData = budgetData.monthlyData?.[yearMonth] || {};
        budgetData = {
          ...budgetData,
          monthlyData: {
            ...budgetData.monthlyData,
            [yearMonth]: {
              ...currentMonthData,
              actualSales: refreshedActualSales
            }
          }
        };

        if (showLogs) {
          logger.debug(`[TeamPerformanceRefreshService] ${yearMonth} actualSales 캐시 로컬 budgetData에 병합 완료`);
        }
      }

      // 🛡️ 오귀속 의심 게이트 (판정 정본: ActualSalesService.isSuspectMisattribution):
      // 진료의사 매핑 부재 창에서 계산된 '미지정' 귀속 결과는 영속(teamPerformanceData 저장·웹훅
      // 전송) 금지 — 화면 표시는 유지하고, 매핑 복구 후 새로고침 시 정상 저장된다.
      // 캐시 경유 결과(loadCachedActualSales)는 플래그가 없으므로(undefined) 여기서 재판정한다 —
      // 플래그 부재를 '의심 없음'으로 오독하면 forceRefresh:false 경로가 게이트를 우회한다.
      const suspectMisattribution = calculatedData.actualSalesResult
        ? (calculatedData.actualSalesResult.suspectMisattribution
            ?? ActualSalesService.isSuspectMisattribution(calculatedData.actualSalesResult, budgetData, yearMonth))
        : false;
      if (suspectMisattribution) {
        logger.warn(
          `[TeamPerformanceRefreshService] ${yearMonth} 진료의사 매핑 부재 상태에서 '미지정' 귀속 매출이 감지되어 ` +
          `팀 성과 저장·웹훅 전송을 건너뜁니다 (화면 표시만 유지). 매핑 복구 후 다시 새로고침하세요.`
        );
      }

      // 데이터 저장 (옵션에 따라, 오귀속 의심 시 스킵)
      if (saveData && !suspectMisattribution) {
        try {
          // 🔧 actualSales 캐시 React 상태 반영 (함수형 패치, 파이프라인 소유 필드만 갱신):
          // Firebase에 이미 저장된 캐시를 상태에도 미러링해, 다음 refresh에서
          // loadCachedActualSales 캐시 미스로 인한 불필요한 재계산을 방지한다.
          // 오귀속 의심 시에는 Firebase 캐시 저장도 스킵되므로 같은 게이트 아래에서만 반영.
          if (refreshedActualSales) {
            const actualSalesPatch = refreshedActualSales;
            setBudgetData((prev: BudgetData) => {
              const prevMonthlyData: Record<string, any> =
                prev?.monthlyData && typeof prev.monthlyData === 'object' ? prev.monthlyData : {};
              const prevMonthData: any =
                prevMonthlyData[yearMonth] && typeof prevMonthlyData[yearMonth] === 'object'
                  ? prevMonthlyData[yearMonth] : {};

              return {
                ...prev,
                monthlyData: {
                  ...prevMonthlyData,
                  [yearMonth]: {
                    ...prevMonthData,
                    actualSales: actualSalesPatch
                  }
                }
              };
            });
          }

          await TeamPerformanceService.saveTeamPerformanceData(
            yearMonth,
            calculatedData.teamPerformanceData,
            totalActualSales,
            calculatedData.teamTargetSales || {},
            budgetData,
            setBudgetData
          );

          if (showLogs) {
            logger.debug(`[TeamPerformanceRefreshService] ${yearMonth} 데이터 저장 완료`);
          }
        } catch (saveError) {
          logger.error(`[TeamPerformanceRefreshService] ${yearMonth} 데이터 저장 실패:`, saveError);
          // 저장 실패해도 계산된 데이터는 반환
        }
      }

      if (showLogs) {
        logger.debug(`[TeamPerformanceRefreshService] ${yearMonth} 새로고침 완료`);
      }

      return {
        performanceData: performanceArray,
        totalActualSales,
        totalWorkingDays,
        lastCalculatedAt,
        dataSource: 'calculated',
        success: true,
        suspectMisattribution
      };

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '알 수 없는 오류가 발생했습니다';
      logger.error(`[TeamPerformanceRefreshService] ${yearMonth} 새로고침 실패:`, error);

      return {
        performanceData: [],
        totalActualSales: 0,
        totalWorkingDays: 0,
        lastCalculatedAt: '',
        dataSource: 'none',
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * Firebase에 캐시보다 새로운 데이터가 있는지 확인합니다
   * @returns true: 새 데이터 있음 (계산 필요), false: 새 데이터 없음 (캐시 사용 가능)
   */
  private static async checkFirebaseForNewerData(
    yearMonth: string,
    budgetData: BudgetData,
    showLogs: boolean
  ): Promise<boolean> {
    // 1. Firebase 최신 업데이트 시간 확인
    let latestFirebaseUpdate: Date | null;
    try {
      latestFirebaseUpdate = await ActualSalesService.getLatestDailyReportUpdateTime(yearMonth);
    } catch (error: any) {
      if (error?.code === 'permission-denied') {
        if (showLogs) {
          logger.warn(`[TeamPerformanceRefreshService] ${yearMonth} Firebase 권한 오류 — 캐시 사용 안 함, 재계산 필요`);
        }
        return true; // 에러 시 재계산 강제 (캐시 중독 방지)
      }
      throw error; // 예상치 못한 에러는 상위로 전파
    }

    if (!latestFirebaseUpdate) {
      if (showLogs) {
        logger.debug(`[TeamPerformanceRefreshService] ${yearMonth} Firebase에 데이터 없음`);
      }
      return false; // 진짜 데이터 없음 → 캐시 사용
    }

    // 2. 캐시 시간 확인
    const cachedAt = budgetData?.monthlyData?.[yearMonth]?.calculatedAt;
    if (!cachedAt) {
      if (showLogs) {
        logger.debug(`[TeamPerformanceRefreshService] ${yearMonth} 캐시 없음 → Firebase 데이터 사용`);
      }
      return true; // 캐시 없음 → 계산 필요
    }

    // 3. 시간 비교
    const cacheTime = new Date(cachedAt);
    const isNewerData = latestFirebaseUpdate > cacheTime;

    if (showLogs) {
      logger.debug(`[TeamPerformanceRefreshService] ${yearMonth} Firebase vs 캐시 비교:`, {
        Firebase최신: latestFirebaseUpdate.toISOString(),
        캐시시간: cacheTime.toISOString(),
        결과: isNewerData ? 'Firebase 더 최신 → 재계산' : '캐시 사용'
      });
    }

    return isNewerData;
  }

  /**
   * 캐시된 데이터를 로드합니다
   */
  private static loadCachedData(
    yearMonth: string,
    budgetData: BudgetData,
    showLogs: boolean
  ): TeamPerformanceRefreshResult | null {
    try {
      const monthlyData = budgetData?.monthlyData?.[yearMonth];
      
      if (monthlyData?.teamPerformanceData &&
          this.safeObjectKeys(monthlyData.teamPerformanceData).length > 0) {

        const totalActualSales = this.safeNumber(monthlyData.totalActualSales);
        const totalWorkingDays = this.safeNumber(monthlyData.totalWorkingDays);

        // 캐시 유효성 검증: performanceData는 있지만 핵심 값이 모두 0인 경우
        // → 불완전한 캐시로 판단하여 재계산 유도
        if (totalWorkingDays === 0 && totalActualSales === 0) {
          if (showLogs) {
            logger.debug(
              `[TeamPerformanceRefreshService] ${yearMonth} 캐시 데이터 불완전 ` +
              `(근무일=0, 실적=0) - 재계산 필요`
            );
          }
          return null;
        }

        if (showLogs) {
          logger.debug(`[TeamPerformanceRefreshService] ${yearMonth} 캐시된 데이터 사용`);
        }

        const performanceArray = this.safeObjectToArray(monthlyData.teamPerformanceData);

        return {
          performanceData: performanceArray,
          totalActualSales,
          totalWorkingDays,
          lastCalculatedAt: monthlyData.calculatedAt || '',
          dataSource: 'cached',
          success: true
        };
      }
    } catch (error) {
      logger.error('[TeamPerformanceRefreshService] 캐시된 데이터 로드 실패:', error);
    }
    
    return null;
  }

  /**
   * 필수 데이터의 유효성을 검사합니다
   */
  private static validateRequiredData(
    currentDate: Date,
    teams: TeamData[],
    employees: Employee[],
    budgetData: BudgetData
  ): { isValid: boolean; error?: string } {
    if (!currentDate) {
      return { isValid: false, error: '현재 날짜가 유효하지 않습니다' };
    }

    if (!Array.isArray(teams) || teams.length === 0) {
      return { isValid: false, error: '팀 데이터가 없습니다' };
    }

    if (!Array.isArray(employees) || employees.length === 0) {
      return { isValid: false, error: '직원 데이터가 없습니다' };
    }

    if (!budgetData || typeof budgetData !== 'object') {
      return { isValid: false, error: '예산 데이터가 없습니다' };
    }

    return { isValid: true };
  }

  /**
   * 안전한 Object.keys 호출 - null/undefined 객체 접근 에러 방지
   */
  private static safeObjectKeys(obj: any): string[] {
    try {
      if (obj === null || obj === undefined || typeof obj !== 'object') {
        return [];
      }
      return Object.keys(obj);
    } catch (error) {
      logger.error('[TeamPerformanceRefreshService] Object.keys 에러:', error);
      return [];
    }
  }

  /**
   * 안전한 Object.values 호출 - null/undefined 객체 접근 에러 방지
   */
  private static safeObjectToArray(obj: any): TeamPerformanceData[] {
    try {
      if (obj === null || obj === undefined || typeof obj !== 'object') {
        return [];
      }
      const values = Object.values(obj);
      return Array.isArray(values) ? values as TeamPerformanceData[] : [];
    } catch (error) {
      logger.error('[TeamPerformanceRefreshService] Object.values 에러:', error);
      return [];
    }
  }

  /**
   * 안전한 숫자 변환
   */
  private static safeNumber(value: any): number {
    if (typeof value === 'number' && !isNaN(value)) {
      return value;
    }
    return 0;
  }

  /**
   * 현재 새로고침 상태를 확인합니다
   */
  static isCurrentlyRefreshing(): boolean {
    return this.isRefreshing;
  }

  /**
   * 특정 월의 팀 성과 데이터 존재 여부를 확인합니다
   */
  static hasTeamPerformanceData(yearMonth: string, budgetData: BudgetData): boolean {
    try {
      const monthlyData = budgetData?.monthlyData?.[yearMonth];
      return !!(monthlyData?.teamPerformanceData && 
                this.safeObjectKeys(monthlyData.teamPerformanceData).length > 0);
    } catch (error) {
      logger.error('[TeamPerformanceRefreshService] 데이터 존재 여부 확인 실패:', error);
      return false;
    }
  }

  /**
   * 여러 월의 데이터를 일괄 새로고침합니다
   */
  static async refreshMultipleMonths(
    months: Date[],
    teams: TeamData[],
    employees: Employee[],
    budgetData: BudgetData,
    setBudgetData: (data: BudgetData | ((prev: BudgetData) => BudgetData)) => void,
    options: TeamPerformanceRefreshOptions = {}
  ): Promise<Record<string, TeamPerformanceRefreshResult>> {
    const results: Record<string, TeamPerformanceRefreshResult> = {};

    // 순차적으로 처리 (동시 처리 시 Firebase 부하 방지)
    for (const month of months) {
      const yearMonth = format(month, 'yyyy-MM');
      try {
        results[yearMonth] = await this.refreshTeamPerformanceData(
          month,
          teams,
          employees,
          budgetData,
          setBudgetData,
          options
        );
        
        // 각 월 사이에 짧은 지연 추가
        await new Promise(resolve => setTimeout(resolve, 100));
      } catch (error) {
        results[yearMonth] = {
          performanceData: [],
          totalActualSales: 0,
          totalWorkingDays: 0,
          lastCalculatedAt: '',
          dataSource: 'none',
          success: false,
          error: error instanceof Error ? error.message : '새로고침 실패'
        };
      }
    }

    return results;
  }
} 